import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import contentService, { Hero, GalleryItem, MenuItem } from '../services/contentService';
import { getImageUrl } from '../services/api';
import { translateWithCache } from '../services/translation';
import Footer from '../components/layout/Footer';
import MenuCard from '../components/MenuCard';
import Popup from '../components/Popup';

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [heroes, setHeroes] = useState<Hero[]>([]);
  const [featuredGallery, setFeaturedGallery] = useState<GalleryItem[]>([]);
  const [featuredMenu, setFeaturedMenu] = useState<MenuItem[]>([]);
  const [menuCategories, setMenuCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  
  // Popup state
  const [popups, setPopups] = useState<any[]>([]);
  const [currentPopup, setCurrentPopup] = useState<any>(null);
  const [showPopup, setShowPopup] = useState(false);
  
  // Drag scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
      const fetchHomeData = async () => {
    try {
      const [heroesData, galleryData, menuData, categoriesData, popupsData] = await Promise.all([
        contentService.getHeroes(),
        contentService.getGalleryItems(),
        contentService.getMenuItems({ featured: true }),
        contentService.getMenuCategories(),
        contentService.getPopups(),
      ]);

      const lang = (i18n.language || 'es').split('-')[0];

      // Translate content when language is English
      let translatedHeroes = heroesData;
      let translatedGallery = galleryData.slice(0, 15);
      let translatedMenu = menuData.slice(0, 6);
      let translatedCategories = categoriesData;
      let translatedPopups = popupsData;

      if (lang === 'en') {
        // Translate heroes
        translatedHeroes = await Promise.all(
          heroesData.map(async (hero) => ({
            ...hero,
            title: hero.title ? await translateWithCache(hero.title, 'es', 'en') : hero.title,
            subtitle: hero.subtitle ? await translateWithCache(hero.subtitle, 'es', 'en') : hero.subtitle,
            button_text: hero.button_text ? await translateWithCache(hero.button_text, 'es', 'en') : hero.button_text,
          }))
        );

        // Translate gallery items
        translatedGallery = await Promise.all(
          galleryData.slice(0, 15).map(async (item) => ({
            ...item,
            title: item.title ? await translateWithCache(item.title, 'es', 'en') : item.title,
            description: item.description ? await translateWithCache(item.description, 'es', 'en') : item.description,
          }))
        );

        // Translate menu items
        translatedMenu = await Promise.all(
          menuData.slice(0, 6).map(async (item) => ({
            ...item,
            name: await translateWithCache(item.name, 'es', 'en'),
            description: item.description ? await translateWithCache(item.description, 'es', 'en') : item.description,
          }))
        );

        // Translate menu categories
        translatedCategories = await Promise.all(
          categoriesData.map(async (category) => ({
            ...category,
            name: await translateWithCache(category.name, 'es', 'en'),
            description: category.description ? await translateWithCache(category.description, 'es', 'en') : category.description,
          }))
        );

        // Translate popups
        translatedPopups = await Promise.all(
          popupsData.map(async (popup) => ({
            ...popup,
            title: popup.title ? await translateWithCache(popup.title, 'es', 'en') : popup.title,
            description: popup.description ? await translateWithCache(popup.description, 'es', 'en') : popup.description,
          }))
        );
      }

      setHeroes(translatedHeroes);
      setFeaturedGallery(translatedGallery);
      setFeaturedMenu(translatedMenu);
      setMenuCategories(translatedCategories);
      setPopups(translatedPopups);
    } catch (err: any) {
      console.error('Error fetching home data:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

    fetchHomeData();
  }, [i18n.language]);

  useEffect(() => {
    if (heroes.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroIndex((prev) => (prev + 1) % heroes.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [heroes.length]);

  // Drag scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (e.currentTarget as HTMLElement).offsetLeft);
    setScrollLeft((e.currentTarget as HTMLElement).scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (e.currentTarget as HTMLElement).offsetLeft;
    const walk = (x - startX) * 2;
    (e.currentTarget as HTMLElement).scrollLeft = scrollLeft - walk;
  };

  // Popup display logic
  useEffect(() => {
    if (popups.length > 0) {
      // Show the first active popup after a short delay
      const timer = setTimeout(() => {
        const activePopup = popups.find(popup => popup.is_active);
        if (activePopup) {
          setCurrentPopup(activePopup);
          setShowPopup(true);
        }
      }, 2000); // Show popup after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [popups]);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }



  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Hero Section - Updated to match exact HTML structure */}
      <Box
        component="section"
        id="inicio"
        sx={{
          position: 'relative',
          backgroundColor: '#FFF0E5',
          minHeight: '70vh',
          overflowX: 'hidden',
        }}
      >
        {/* Background coffee elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          opacity: 0.05,
          zIndex: 0,
          overflow: 'hidden',
        }}>
          <Box sx={{ mt: 8 }}>
            <img 
              src="/images/left-coffee.svg" 
              alt="" 
              style={{ width: '200px', maxWidth: '100%', height: 'auto' }}
            />
          </Box>
          <Box sx={{ mt: 8 }}>
            <img 
              src="/images/right-coffee.svg" 
              alt="" 
              style={{ width: '500px', maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </Box>

        {/* Background color */}
        <Box sx={{
          position: 'absolute',
          backgroundColor: '#FFF0E5',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }} />

        {/* Main content container */}
        <Container maxWidth="lg" sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: { xs: 12, md: 15 },
          pb: 10,
          position: 'relative',
          zIndex: 2,
        }}>
          {/* Left side - Content */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'center', md: 'flex-start' },
            alignItems: { xs: 'center', md: 'flex-start' },
            px: { xs: 3, sm: 0, md: 0 },
            position: 'relative',
          }}>
            {/* Main headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3rem', md: '4.5rem' },
                textAlign: { xs: 'center', md: 'left' },
                fontFamily: '"Bebas Neue", sans-serif',
                fontWeight: 400,
                width: '250px',
                lineHeight: 1.1,
                mb: 2,
              }}
            >
              <Box component="span" sx={{
                fontFamily: '"Hey August", cursive',
                textTransform: 'uppercase',
                color: '#009F9A',
                display: 'block',
              }}>
                {t('home.hero.top')}
              </Box>
              {t('home.hero.bottomLine1')} <br />
              {t('home.hero.bottomLine2')}
            </Typography>

            {/* Subtitle - hidden on mobile */}
            <Typography
              variant="h2"
              sx={{
                fontSize: '1.5rem',
                width: '66.666667%',
                my: 2.5,
                display: { xs: 'none', md: 'block' },
                color: '#000000',
                lineHeight: 1.4,
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
              }}
            >
              {t('home.hero.subtitle')}
            </Typography>

            {/* Call to action button - hidden on mobile */}
            <Box sx={{ mt: 2, display: { xs: 'none', md: 'block' } }}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/menu"
                sx={{
                  backgroundColor: '#F89C24',
                  color: 'white',
                  px: 4,
                  py: 0.5,
                  fontSize: '1.5rem',
                  fontWeight: 'normal',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Bebas Neue", sans-serif',
                  '&:hover': {
                    backgroundColor: '#F89C24',
                    opacity: 0.9,
                  },
                }}
              >
                {t('home.hero.cta')}
              </Button>
            </Box>

            {/* Hashtag - hidden on mobile */}
            <Box sx={{ mt: 5, display: { xs: 'none', md: 'block' } }}>
              <Typography sx={{
                fontFamily: '"Hey August", cursive',
                textTransform: 'uppercase',
                color: '#F89C24',
                fontSize: '1.875rem',
              }}>
                {t('footer.hashtag')}
              </Typography>
            </Box>
          </Box>

          {/* Center - Coffee beans image */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            alignItems: 'flex-start',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 100,
            zIndex: 1,
            animation: 'float 2s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px)',
              },
              '50%': {
                transform: 'translateY(-15px)',
              },
            },
            mt: 0,
            pr: 0,
            ml: 'auto',
          }}>
            <img 
              src="/images/coffee-beans.png" 
              alt="" 
              style={{ width: '250px', height: 'auto' }}
              className="coffee-seeds-image"
            />
          </Box>

          {/* Right side - Coffee cup and background */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'relative',
            width: '100%',
            height: '100%',
          }}>
            {/* Coffee cup */}
            <Box sx={{
              position: { xs: 'static', md: 'absolute' },
              top: { md: '-40px' },
              right: { md: '64px' },
              mx: { xs: 'auto', md: 0 },
              width: '300px',
            }}>
              <img 
                src="/images/coffee.png" 
                alt="" 
                className="coffee-cup"
                style={{ width: '100%', height: 'auto' }}
              />
            </Box>

            {/* Mobile spacer */}
            <Box sx={{ 
              display: { xs: 'none', md: 'none' }, 
              width: '100%', 
              height: '0px' 
            }} />

            {/* SVG background - hidden on mobile */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <svg 
                width="400" 
                height="auto" 
                viewBox="0 0 956 839" 
                fill="#F89C24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M737.607 22.9544L737.358 22.6415C626.521 -21.3752 467.922 -1.4408 314.825 84.4376C81.1207 215.335 -51.4 454.919 18.7941 619.463C89.0057 783.955 335.343 811.084 569.048 679.99C750.502 578.285 870.992 411.162 882.907 264.502L883.028 264.537C910.429 72.6079 739.37 23.3831 737.613 22.9544H737.607ZM494.134 371.815C271.509 404.031 160.713 560.476 153.651 543.792C145.459 524.773 214.285 393.29 450.25 340.248C680.812 288.393 777.688 186.983 826.674 133.604C829.735 130.233 831.921 127.093 833.677 124.017C843.603 140.116 851.442 152.467 850.995 175.113C797.638 235.565 704.16 341.551 494.128 371.815H494.134Z" />
              </svg>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Box 
        component="section" 
        id="nosotros" 
        sx={{ 
          position: 'relative',
          mt: { xs: 44, md: 0 },
          px: { xs: 3, sm: 0, md: 0 },
          backgroundColor: 'white',
        }}
      >
        {/* Background coffee elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          opacity: 0.1,
          zIndex: -1,
          overflow: 'hidden',
        }}>
          <Box sx={{ mt: 8 }}>
            <img 
              src="/images/right-coffee.svg" 
              alt="" 
              style={{ width: '500px', maxWidth: '100%', height: 'auto' }}
            />
          </Box>
          <Box sx={{ mt: 56 }}>
            <img 
              src="/images/left-coffee.svg" 
              alt="" 
              style={{ width: '200px', maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" sx={{ pt: { xs: 2.5, md: 10 }, pb: 10 }}>
            {/* Left side - Image (hidden on mobile) */}
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src="/images/about-img.png"
                alt="Comida Tosto Coffee"
                sx={{
                  transform: 'rotate(-6deg)',
                  borderRadius: '16px',
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                }}
              />
            </Grid>

            {/* Right side - Content */}
            <Grid item xs={12} md={6} sx={{ maxWidth: { md: '600px' } }}>
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: '3.75rem',
                    fontFamily: '"Bebas Neue", sans-serif',
                    color: '#78350F',
                    mb: 1,
                  }}
                >
                  Tosto Coffee
                </Typography>
                
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 400,
                    fontStyle: 'italic',
                    mb: 2.5,
                    color: '#000000',
                    fontFamily: '"Poppins", sans-serif',
                  }}
                >
                  {t('home.about.tagline')}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.25rem',
                      color: '#000000',
                      textAlign: 'justify',
                      mb: 1.5,
                      lineHeight: 1.6,
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 'bold' }}>Tosto Coffee</Box> {t('home.about.p1')}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.25rem',
                      color: '#000000',
                      textAlign: 'justify',
                      mb: 1.5,
                      lineHeight: 1.6,
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 'bold' }}>{t('home.about.ourCoffeeTitle')}</Box>
                    <br />
                    {t('home.about.ourCoffeeText')}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.25rem',
                      color: '#000000',
                      textAlign: 'justify',
                      mb: 1.5,
                      lineHeight: 1.6,
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 'bold' }}>{t('home.about.ourAmbienceTitle')}</Box>
                    <br />
                    {t('home.about.ourAmbienceText')}
                  </Typography>
                </Box>

                <Box sx={{ position: 'relative' }}>
                  <Typography
                    sx={{
                      fontFamily: '"Hey August", cursive',
                      fontSize: '3rem',
                      color: '#F89C24',
                      mb: 2,
                      lineHeight: 1.2,
                    }}
                  >
                    {t('home.about.specialMomentsLine1')} <br /> {t('home.about.specialMomentsLine2')}
                  </Typography>
                </Box>
                
                <Box
                  component="img"
                  src="/images/coffee-seed.png"
                  alt=""
                  sx={{
                    width: '250px',
                    position: 'absolute',
                    bottom: { xs: -24, md: 0 },
                    right: 0,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Menu Section */}
      <Box 
        component="section" 
        id="menu" 
        sx={{ 
          position: 'relative',
          backgroundColor: 'white',
        }}
      >
        {/* Background coffee elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          opacity: 0.05,
          zIndex: -1,
          overflow: 'hidden',
        }}>
          <Box>
            <img 
              src="/images/bg-coffee-elements.svg" 
              alt="" 
              style={{ width: '800px', maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </Box>

        {/* Background gradient */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, transparent, #FFF8E5)',
          opacity: 0.6,
          zIndex: -1,
        }} />

        {/* Header */}
        <Container maxWidth="lg" sx={{ pt: 10, px: { xs: 3, sm: 0, md: 0 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 8,
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex' }}>
                <Link component={RouterLink} to="/menu" sx={{ textDecoration: 'none' }}>
          <Typography
                    variant="h4"
            sx={{
              fontFamily: '"Bebas Neue", sans-serif',
                      textTransform: 'uppercase',
                      fontSize: '3rem',
                      color: 'white',
                      backgroundColor: '#78350F',
                      px: 2.5,
                      py: 1,
                    }}
                  >
                    {t('home.menu.title')}
          </Typography>
                </Link>
              </Box>
              <Box sx={{ backgroundColor: '#009F9A', px: 2.5, py: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: '"Hey August", cursive',
                    fontSize: '1.875rem',
                    color: 'white',
                  }}
                >
                  {t('home.menu.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Box
                component="img"
                src="/images/logo.svg"
                alt="Logo"
                sx={{ width: '150px' }}
              />
            </Box>
          </Box>
        </Container>

        {/* Menu Carousel */}
        <Box sx={{ position: 'relative' }}>
          <Box
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 3,
              pb: 2,
              px: 3,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              WebkitOverflowScrolling: 'touch',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
            }}
          >
            {menuCategories.map((category, index) => (
                <Box
                  key={category.id || index}
                  sx={{
                    minWidth: '345px',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: '345px',
                      height: '610px',
                      backgroundColor: '#009F9A',
                      borderRadius: '16px',
                      border: '1px solid #009388',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 10,
                      px: 4,
                    }}
                  >
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <Box
                        component="img"
                        src={category.image_url ? getImageUrl(category.image_url) : '/images/menu/thumbs/breakfast.jpg'}
                        alt=""
                        sx={{
                          borderRadius: '50%',
                          width: '200px',
                          height: '200px',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            color: 'black',
                            fontFamily: '"Bebas Neue", sans-serif',
                            fontSize: '29px',
                            mb: 1,
                          }}
                        >
                          {category.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '17px',
                            mb: 2.5,
                          }}
                        >
                          {category.description}
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                          <svg width="100" viewBox="0 0 231 34" fill="none">
                            <path d="M17 24.4658L25.755 29.7499L23.4316 19.7908L31.1666 13.0899L20.9808 12.2116L17 2.83325L13.0191 12.2116L2.83331 13.0899L10.5541 19.7908L8.24498 29.7499L17 24.4658Z" fill="#F89C24"/>
                            <path d="M64 24.4658L72.755 29.7499L70.4316 19.7908L78.1666 13.0899L67.9808 12.2116L64 2.83325L60.0191 12.2116L49.8333 13.0899L57.5541 19.7908L55.245 29.7499L64 24.4658Z" fill="#F89C24"/>
                            <path d="M114 24.4658L122.755 29.7499L120.432 19.7908L128.167 13.0899L117.981 12.2116L114 2.83325L110.019 12.2116L99.8333 13.0899L107.554 19.7908L105.245 29.7499L114 24.4658Z" fill="#F89C24"/>
                            <path d="M164 24.4658L172.755 29.7499L170.432 19.7908L178.167 13.0899L167.981 12.2116L164 2.83325L160.019 12.2116L149.833 13.0899L157.554 19.7908L155.245 29.7499L164 24.4658Z" fill="#F89C24"/>
                            <path d="M214 24.4658L222.755 29.7499L220.432 19.7908L228.167 13.0899L217.981 12.2116L214 2.83325L210.019 12.2116L199.833 13.0899L207.554 19.7908L205.245 29.7499L214 24.4658Z" fill="#F89C24"/>
                          </svg>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                        <Button
                          component={RouterLink}
                          to={`/menu/${category.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                          variant="contained"
                          sx={{
                            backgroundColor: 'black',
                            color: 'white',
                            fontSize: '20px',
                            px: 6,
                            py: 1,
                            fontFamily: '"Bebas Neue", sans-serif',
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: 'black',
                              opacity: 0.9,
                            },
                          }}
                        >
                          {t('home.hero.cta')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                        ))}
            </Box>
          </Box>

        {/* Club Elite Section */}
        <Container maxWidth="lg" sx={{ py: 20, px: { xs: 3, md: 0 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            p: { xs: 3, md: 10 },
            backgroundColor: '#523629',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}>
            <Box>
              <Box
                component="img"
                src="/images/club-elite-logo.png"
                alt="Club Elite"
                sx={{
                  backgroundColor: 'white',
                  width: '300px',
                  p: 2.5,
                  borderRadius: '16px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }}
              />
          <Typography
                variant="body2"
            sx={{
                  color: 'white',
                  opacity: 0.6,
                  mt: 3.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('home.clubElite.disclaimer')}
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              textAlign: { xs: 'left', md: 'right' },
            }}>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontSize: '3.75rem',
              fontFamily: '"Bebas Neue", sans-serif',
                  mt: { xs: 1.5, md: 0 },
            }}
          >
                {t('home.clubElite.title')}
          </Typography>
              <Typography
                variant="h5"
                  sx={{
                  fontFamily: '"Hey August", cursive',
                  fontSize: '3rem',
                  color: '#F89C24',
                }}
              >
                {t('home.clubElite.subtitle')}
              </Typography>
              <Box sx={{
                width: '80px',
                height: '2px',
                backgroundColor: 'white',
                my: 3,
                ml: { xs: 0, md: 'auto' },
              }} />
              <Typography
                variant="body1"
                sx={{
                  color: 'white',
                  fontSize: '1.25rem',
                }}
              >
                {t('home.clubElite.benefitLine1')} <br />
                {t('home.clubElite.benefitLine2')}
              </Typography>
              <Button
                component="a"
                href="https://club.tostocoffee.com/registro-club-elite"
                variant="contained"
                sx={{
                  backgroundColor: '#F89C24',
                  color: 'white',
                  fontSize: '1.5rem',
                  px: 5,
                  py: 1,
                  mt: 2,
                  fontFamily: '"Bebas Neue", sans-serif',
                  textTransform: 'none',
                    '&:hover': {
                    backgroundColor: '#F89C24',
                    opacity: 0.9,
                    },
                  }}
                >
                {t('home.clubElite.cta')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Gallery Section */}
      <Box 
        component="section" 
        id="galeria" 
        sx={{ 
          backgroundColor: 'white',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 10 }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 8,
            px: { xs: 3, sm: 0, md: 0 },
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex' }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: '"Bebas Neue", sans-serif',
                    textTransform: 'uppercase',
                    fontSize: '3rem',
                    color: 'white',
                    backgroundColor: '#78350F',
                    px: 2.5,
                    py: 1,
                  }}
                >
                  {t('home.gallery.title')}
                </Typography>
              </Box>
              <Box sx={{ backgroundColor: '#009F9A', px: 2.5, py: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: '"Hey August", cursive',
                    fontSize: '1.875rem',
                    textTransform: 'uppercase',
                    color: 'white',
                  }}
                >
                  {t('footer.hashtag')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Gallery Grid */}
          <Grid container spacing={5} sx={{ px: { xs: 3, sm: 0, md: 0 } }}>
            {featuredGallery.map((item, index) => (
              <Grid item xs={12} md={4} key={item.id || index}>
                <Box
                  component="img"
                  src={getImageUrl(item.image_url)}
                  alt={item.title || `Gallery image ${index + 1}`}
                  sx={{
                    borderRadius: '16px',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Instagram Link */}
          <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
            <Link
              href="https://www.instagram.com/tostocoffee/"
              target="_blank"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': {
                  '& .instagram-text': {
                    color: '#F89C24',
                  },
                },
              }}
            >
              <Box sx={{ mr: 2.5 }}>
                <svg width="30" height="30" fill="#009F9A" viewBox="0 0 24 24">
                  <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/>
                </svg>
              </Box>
              <Typography
                className="instagram-text"
                sx={{
                  fontSize: '1.5rem',
                  textTransform: 'uppercase',
                  fontFamily: '"Hey August", cursive',
                  color: '#009F9A',
                  transition: 'color 0.3s ease',
                }}
              >
                {t('home.gallery.instagramCta')}
              </Typography>
            </Link>
          </Box>
        </Container>
      </Box>

      {/* Branches Section */}
      <Box 
        component="section" 
        id="sucursales" 
                    sx={{
          position: 'relative',
          py: [10, 10],
          overflow: 'hidden',
          backgroundColor: '#513628',
        }}
      >
        {/* Background burger elements */}
        <Box sx={{
                      position: 'absolute',
          top: 0,
                      left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: 0.15,
          zIndex: -1,
        }}>
          <img 
            src="/images/bg-burger-elements.svg" 
            alt="" 
            style={{ 
              width: '100%', 
              height: 'auto',
              maxWidth: '1200px'
            }}
          />
        </Box>

        {/* Background color */}
        <Box sx={{
          position: 'absolute',
          backgroundColor: '#513628',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -2,
        }} />

        {/* Header */}
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            gap: 2,
          }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Bebas Neue", sans-serif',
                color: '#009F9A',
                fontSize: '3.75rem',
              }}
            >
              {t('home.branches.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Hey August", cursive',
                      color: 'white',
                fontSize: '4.375rem',
                textTransform: 'uppercase',
              }}
            >
                                {t('home.branches.subtitle')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textTransform: 'uppercase',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#F89C24',
              }}
            >
              {t('home.branches.tagline')}
                    </Typography>
                  </Box>
        </Container>

        {/* Branches Carousel */}
        <Box sx={{ mt: 4, position: 'relative' }}>
          <Container sx={{ maxWidth: '80%', mx: 'auto' }}>
            <Grid container spacing={10}>
              {[
                {
                  name: 'Alta Plaza Mall',
                  location: 'AltaPlaza Mall, Nivel 1',
                  image: '/images/branches/altaplaza-mall.png',
                  hours: [
                    { text: t('home.branches.hours.allWeek'), time: '9:00 am a 10:00 pm', color: '#F89C24' }
                  ],
                  googleMaps: 'https://maps.app.goo.gl/rHJhbNHzumxXYiCW6',
                  waze: 'waze://?q=Tosto Coffee Altaplaza Mall'
                },
                {
                  name: 'Centennial Center',
                  location: 'Condado del Rey, Plaza de Global Bank, Panamá',
                  image: '/images/branches/centenial.png',
                  hours: [
                    { text: t('home.branches.hours.mondayClosed'), time: '', color: '#BBBBBB' },
                    { text: t('home.branches.hours.tueToFri'), time: '7:00 am a 10:00 pm', color: '#F89C24' },
                    { text: t('home.branches.hours.satSun'), time: '8:00 am a 10:00 pm', color: '#F89C24' }
                  ],
                  googleMaps: 'https://goo.gl/maps/noiTSQbsu8wxAMfv8?coh=178573&entry=tt',
                  waze: 'waze://?q=Tosto Coffee Co. Centennial'
                },
                {
                  name: 'San Francisco',
                  location: 'Calle 74. Plaza Spot 74, San Francisco, Panamá',
                  image: '/images/branches/san-francisco.png',
                  hours: [
                    { text: t('home.branches.hours.monToFri'), time: '7:00 am a 10:00 pm', color: '#F89C24' },
                    { text: t('home.branches.hours.satSun'), time: '8:00 am a 10:00 pm', color: '#F89C24' }
                  ],
                  googleMaps: 'https://goo.gl/maps/SfzQodhhNq7XCQjW9?coh=178573&entry=tt',
                  waze: 'waze://?q=Tosto Coffee San Francisco'
                },
                {
                  name: 'Calle Uruguay',
                  location: 'Av Federico Boyd y Calle 49 Este, Bella Vista, Panamá',
                  image: '/images/branches/calle-uruguay.png',
                  hours: [
                    { text: t('home.branches.hours.mondayClosed'), time: '', color: '#BBBBBB' },
                    { text: t('home.branches.hours.tueToFri'), time: '7:00 am a 10:00 pm', color: '#F89C24' },
                    { text: t('home.branches.hours.satSun'), time: '8:00 am a 10:00 pm', color: '#F89C24' }
                  ],
                  googleMaps: 'https://goo.gl/maps/zxALbH6mrKbMVs7W8?coh=178573&entry=tt',
                  waze: 'waze://?q=Tosto Coffee Calle Uruguay'
                }
              ].map((branch, index) => (
                <Grid item xs={12} md={3} key={index}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                    {/* Branch Image */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        component="img"
                        src={branch.image}
                        alt={branch.name}
                        sx={{
                          borderRadius: '50%',
                          border: '12px solid white',
                          width: '280px',
                          height: '280px',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>

                    {/* Branch Info */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      mt: 4,
                      width: '100%',
                    }}>
                      {/* Branch Name and Location */}
                      <Box sx={{ textAlign: 'left', width: '100%' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            color: 'white',
                            fontFamily: '"Bebas Neue", sans-serif',
                            fontSize: '2.25rem',
                          }}
                        >
                          {branch.name}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: '1.25rem',
                            fontFamily: '"Poppins", sans-serif',
                            color: 'white',
                          }}
                        >
                          {branch.location}
                        </Typography>
                      </Box>

                      {/* Hours */}
                      <Box sx={{ mt: 5, gap: 3, display: 'flex', flexDirection: 'column' }}>
                        {branch.hours.map((hour, hourIndex) => (
                          <Box key={hourIndex}>
                            <Typography
                              sx={{
                                fontSize: '1.25rem',
                                color: hour.color,
                              }}
                            >
                              {hour.text}
                              {hour.time && <><br />{hour.time}</>}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Navigation Buttons */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 5,
                        mt: 10,
                      }}>
                        {/* Google Maps */}
                        <Link
                          href={branch.googleMaps}
                          target="_blank"
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            p: 2,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                          }}
                        >
                          <svg width="35" height="35" viewBox="0 0 24 24" fill="#dc2626">
                            <path d="M18.27 6C19.28 8.17 19.05 10.73 17.94 12.81C17 14.5 15.65 15.93 14.5 17.5C14 18.2 13.5 18.95 13.13 19.76C13 20.03 12.91 20.31 12.81 20.59C12.71 20.87 12.62 21.15 12.53 21.43C12.44 21.69 12.33 22 12 22H12C11.61 22 11.5 21.56 11.42 21.26C11.18 20.53 10.94 19.83 10.57 19.16C10.15 18.37 9.62 17.64 9.08 16.93L18.27 6M9.12 8.42L5.82 12.34C6.43 13.63 7.34 14.73 8.21 15.83C8.42 16.08 8.63 16.34 8.83 16.61L13 11.67L12.96 11.68C11.5 12.18 9.88 11.44 9.3 10C9.22 9.83 9.16 9.63 9.12 9.43C9.07 9.06 9.06 8.79 9.12 8.43L9.12 8.42M6.58 4.62L6.57 4.63C4.95 6.68 4.67 9.53 5.64 11.94L9.63 7.2L9.58 7.15L6.58 4.62M14.22 2.36L11 6.17L11.04 6.16C12.38 5.7 13.88 6.28 14.56 7.5C14.71 7.78 14.83 8.08 14.87 8.38C14.93 8.76 14.95 9.03 14.88 9.4L14.88 9.41L18.08 5.61C17.24 4.09 15.87 2.93 14.23 2.37L14.22 2.36M9.89 6.89L13.8 2.24L13.76 2.23C13.18 2.08 12.59 2 12 2C10.03 2 8.17 2.85 6.85 4.31L6.83 4.32L9.89 6.89Z" />
                          </svg>
                        </Link>

                        {/* Waze */}
                        <Link
                          href={branch.waze}
                          target="_blank"
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            p: 2,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                          }}
                        >
                          <svg width="35" height="35" viewBox="0 0 24 24" fill="#06b6d4">
                            <path d="M20.54,6.63C21.23,7.57 21.69,8.67 21.89,9.82C22.1,11.07 22,12.34 21.58,13.54C21.18,14.71 20.5,15.76 19.58,16.6C18.91,17.24 18.15,17.77 17.32,18.18C17.73,19.25 17.19,20.45 16.12,20.86C15.88,20.95 15.63,21 15.38,21C14.27,21 13.35,20.11 13.31,19C13.05,19 10.73,19 10.24,19C10.13,20.14 9.11,21 7.97,20.87C6.91,20.77 6.11,19.89 6.09,18.83C6.1,18.64 6.13,18.44 6.19,18.26C4.6,17.73 3.21,16.74 2.19,15.41C1.86,14.97 1.96,14.34 2.42,14C2.6,13.86 2.82,13.78 3.05,13.78C3.77,13.78 4.05,13.53 4.22,13.15C4.46,12.43 4.6,11.68 4.61,10.92C4.64,10.39 4.7,9.87 4.78,9.35C5.13,7.62 6.1,6.07 7.5,5C9.16,3.7 11.19,3 13.29,3C14.72,3 16.13,3.35 17.4,4C18.64,4.62 19.71,5.5 20.54,6.63M16.72,17.31C18.5,16.5 19.9,15.04 20.59,13.21C22.21,8.27 18,4.05 13.29,4.05C12.94,4.05 12.58,4.07 12.23,4.12C9.36,4.5 6.4,6.5 5.81,9.5C5.43,11.5 6,14.79 3.05,14.79C4,16 5.32,16.93 6.81,17.37C7.66,16.61 8.97,16.69 9.74,17.55C9.85,17.67 9.94,17.8 10,17.94C10.59,17.94 13.2,17.94 13.55,17.94C14.07,16.92 15.33,16.5 16.35,17.04C16.5,17.12 16.6,17.21 16.72,17.31M10.97,10.31C10.39,10.34 9.88,9.9 9.85,9.31C9.82,8.73 10.27,8.23 10.85,8.19C11.43,8.16 11.94,8.61 11.97,9.25C12,9.8 11.56,10.27 11,10.29L10.97,10.31M15.66,10.31C15.08,10.34 14.57,9.9 14.54,9.31C14.5,8.73 14.96,8.23 15.54,8.19C16.12,8.16 16.63,8.61 16.66,9.25C16.68,9.8 16.25,10.27 15.66,10.29V10.31M9.71,12.07C9.65,11.79 9.84,11.5 10.12,11.45C10.4,11.4 10.68,11.58 10.74,11.86V11.86C11.09,12.97 12.16,13.69 13.32,13.6C14.46,13.66 15.5,12.96 15.89,11.88C16.03,11.62 16.35,11.5 16.6,11.65C16.78,11.75 16.89,11.92 16.89,12.12C16.7,12.83 16.26,13.45 15.66,13.88C14.97,14.36 14.16,14.63 13.32,14.64H13.21C11.58,14.71 10.11,13.64 9.68,12.06L9.71,12.07Z" />
                          </svg>
                        </Link>
                      </Box>
                    </Box>
                  </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
        </Box>
      </Box>

      {/* Footer */}
      <Footer />

      {/* Popup */}
      {currentPopup && (
        <Popup
          popup={currentPopup}
          open={showPopup}
          onClose={handleClosePopup}
        />
      )}
    </Box>
  );
};

export default HomePage; 